import {HIDDEN_FEATURES, HiddenFeature, MODULE_TYPES, TIERS} from '../../../constants';
import {hasModule} from './ModuleUtil';
import {
	sageCostRestricted,
	sageCreateExpenseRestricted,
	sageCreateInvoiceRestricted,
	sageFinancialsRestricted,
	sageProjectRevenueRecognitionRestricted,
	sageReportingRestricted,
	sageProjectTaskBreakdownRestricted,
	sageAdminFinanceRateCardRestricted,
	sageAdminFinanceCurrenciesRestricted,
	sageAdminFinanceExpenseCategoriesRestricted,
	sageAdminFinanceBudgetToleranceRestricted,
	sageAdminFinanceInvoicingRestricted,
	sageProjectBudgetMoneyRestricted,
	sageRevenueRestricted,
	sageProfitRestricted,
	sageMarginRestricted,
} from './sage/SageRestrictionUtil';

const CACHE = new Map<string, string | boolean | null>();
const COMPANY_SETUP_STORAGE_KEY = 'companySetup';

type Company = {
	clientsEnabled: boolean;
	revenueEnabled: boolean;
	tier: string;
	timesheetsEnabled: boolean;
	weekendDisplayPerUser: boolean;
	weekendDisplayShowAlways: boolean | null;
};

const DEFAULT_SETUP: Company = {
	revenueEnabled: true,
	timesheetsEnabled: true,
	clientsEnabled: true,
	tier: TIERS.LITE,
	weekendDisplayPerUser: true,
	weekendDisplayShowAlways: null,
};

export const COMPANY_SETUP = {
	TIME_SHEETS_ENABLED: 'timesheetsEnabled',
	REVENUE_ENABLED: 'revenueEnabled',
	CLIENTS_ENABLED: 'clientsEnabled',
	TIER: 'tier',
	WEEKEND_DISPLAY_PER_USER: 'weekendDisplayPerUser',
	WEEKEND_DISPLAY_SHOW_ALWAYS: 'weekendDisplayShowAlways',
} as const;

export const setCompanySetup = (company: Company): void => {
	if (company) {
		CACHE.clear();

		const companySetup: Company = {
			revenueEnabled: company.revenueEnabled,
			timesheetsEnabled: company.timesheetsEnabled,
			clientsEnabled: company.clientsEnabled,
			tier: company.tier,
			weekendDisplayPerUser: company.weekendDisplayPerUser,
			weekendDisplayShowAlways: company.weekendDisplayShowAlways,
		};

		sessionStorage.setItem(COMPANY_SETUP_STORAGE_KEY, JSON.stringify(companySetup));

		// populate cache
		for (const [key, value] of Object.entries(companySetup)) {
			CACHE.set(key, value);
		}
	}
};

const setCompanySetupCache = () => {
	if (CACHE.size === 0) {
		const sessionCompanySetup = sessionStorage.getItem(COMPANY_SETUP_STORAGE_KEY);
		const companySetup = sessionCompanySetup ? JSON.parse(sessionCompanySetup) : DEFAULT_SETUP;
		setCompanySetup(companySetup);
	}
};

export const getCompanySetting = <T extends string | boolean>(setting: string): T | null => {
	setCompanySetupCache();
	return (CACHE.get(setting) as T) ?? null;
};

export const setCompanySetting = (setting: string, value: string | boolean): typeof CACHE | null => {
	if (COMPANY_SETUP[setting]) {
		setCompanySetupCache();
		return CACHE.set(setting, value);
	}

	return null;
};

export const isFeatureHidden = (featureName: HiddenFeature): boolean => {
	switch (featureName) {
		case HIDDEN_FEATURES.TIME_REGISTRATIONS:
			return getCompanySetting(COMPANY_SETUP.TIME_SHEETS_ENABLED) === false;
		case HIDDEN_FEATURES.BUDGET_TYPES:
			return getCompanySetting(COMPANY_SETUP.REVENUE_ENABLED) === false;
		case HIDDEN_FEATURES.BUDGET_MONEY:
			return sageProjectBudgetMoneyRestricted();
		case HIDDEN_FEATURES.REVENUE:
			return getCompanySetting(COMPANY_SETUP.REVENUE_ENABLED) === false && sageRevenueRestricted();
		case HIDDEN_FEATURES.CLIENT:
			return getCompanySetting(COMPANY_SETUP.CLIENTS_ENABLED) === false;
		case HIDDEN_FEATURES.COST:
			return sageCostRestricted();
		case HIDDEN_FEATURES.PROFIT:
			return sageProfitRestricted();
		case HIDDEN_FEATURES.MARGIN:
			return sageMarginRestricted();
		case HIDDEN_FEATURES.REVENUE_RECOGNITION:
			return sageProjectRevenueRecognitionRestricted();
		case HIDDEN_FEATURES.TASK_BREAKDOWN:
			return sageProjectTaskBreakdownRestricted();
		case HIDDEN_FEATURES.CREATE_EXPENSE:
			return sageCreateExpenseRestricted();
		case HIDDEN_FEATURES.CREATE_INVOICE:
			return sageCreateInvoiceRestricted();
		case HIDDEN_FEATURES.REPORTING:
			return sageReportingRestricted();
		case HIDDEN_FEATURES.ADMIN_FINANCE_EXPENSE_CATEGORIES:
			return sageAdminFinanceExpenseCategoriesRestricted();
		case HIDDEN_FEATURES.ADMIN_FINANCE_RATE_CARD:
			return getCompanySetting(COMPANY_SETUP.REVENUE_ENABLED) === false && sageAdminFinanceRateCardRestricted();
		case HIDDEN_FEATURES.ADMIN_FINANCE_CURRENCIES:
			return sageAdminFinanceCurrenciesRestricted();
		case HIDDEN_FEATURES.ADMIN_FINANCE_BUDGET_TOLERANCE:
			return sageAdminFinanceBudgetToleranceRestricted();
		case HIDDEN_FEATURES.ADMIN_FINANCE_INVOICING:
			return sageAdminFinanceInvoicingRestricted();
		default:
			return false;
	}
};

export default class CompanySetupUtil {
	static getTier(): string | null {
		return getCompanySetting(COMPANY_SETUP.TIER);
	}
	static hasCost(): boolean {
		return !isFeatureHidden(HIDDEN_FEATURES.COST);
	}
	static hasFinance(): boolean {
		return hasModule(MODULE_TYPES.FINANCE) && !sageFinancialsRestricted();
	}
	static hasFinanceLight(): boolean {
		return this.getTier() === TIERS.LITE;
	}
	static hasRevenueRecognition(): boolean {
		return this.hasFinance() && !isFeatureHidden(HIDDEN_FEATURES.REVENUE_RECOGNITION);
	}
	static hasExpenses(): boolean {
		return this.hasFinance();
	}
	static hasReporting(): boolean {
		return !isFeatureHidden(HIDDEN_FEATURES.REPORTING);
	}
	static hasCreateExpense(): boolean {
		return !isFeatureHidden(HIDDEN_FEATURES.CREATE_EXPENSE) && this.hasExpenses();
	}
	static hasCreateInvoice(): boolean {
		return !isFeatureHidden(HIDDEN_FEATURES.CREATE_INVOICE);
	}
	static getWeekendDisplayPerUser(): boolean | null {
		return getCompanySetting(COMPANY_SETUP.WEEKEND_DISPLAY_PER_USER);
	}
	static getWeekendDisplayShowAlways(): boolean | null {
		return getCompanySetting(COMPANY_SETUP.WEEKEND_DISPLAY_SHOW_ALWAYS);
	}
	static weekendsAreVisiblePerUser(): boolean {
		return getCompanySetting(COMPANY_SETUP.WEEKEND_DISPLAY_PER_USER) === true;
	}
	static weekendsAlwaysVisible(): boolean {
		return getCompanySetting(COMPANY_SETUP.WEEKEND_DISPLAY_SHOW_ALWAYS) === true;
	}
	static weekendsAlwaysHidden(): boolean {
		return getCompanySetting(COMPANY_SETUP.WEEKEND_DISPLAY_SHOW_ALWAYS) === false;
	}
}
